import { LockClosedIcon, UpdateIcon } from '@radix-ui/react-icons';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { DropdownMenuItem } from "../ui/dropdown-menu";
import { Input } from '../ui/input';
import { Label } from '../ui/label';

import api from '../../lib/api';
import { toast } from '../ui/use-toast';

const SuspendMemberDialog = ({ user, fetchClubMembers }) => {

    const [isLoading, setIsLoading] = useState(false);
    const [reason, setReason] = useState('');
    const [isSuspended, setIsSuspended] = useState(false);

    useEffect(() => {
        setReason('');
        setIsSuspended(!!user.suspended_reason); // Check if suspended_reason is not null or empty
    }, [user]);

    const handleSuspend = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await api.post(`https://skhrf-api.cmdigital.se/v1/clubs/suspend`, {
                targetUserId: user.id,
                reason: reason
            });

            fetchClubMembers();
            toast({
                title: 'Användare inaktiverad',
                description: user.first_name + " " + user.last_name + " blev inaktiverad på grund av " + reason,
            });
            setIsLoading(false);
        } catch (error) {
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleActivate = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await api.post(`https://skhrf-api.cmdigital.se/v1/clubs/reinstate`, {
                targetUserId: user.id
            });
            setIsLoading(false);

            fetchClubMembers();
            toast({
                title: 'Användare återaktiverad',
                description: user.first_name + " " + user.last_name + " blev återaktiverad.",
            });
        } catch (error) {
            console.error(error);
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleReasonChange = useCallback((e) => {
        setReason(e.target.value);
    }, []);

    return (
        <Dialog>
            <DropdownMenuItem
                className={`text-${isSuspended ? 'default' : 'destructive'} w-full`}
                onSelect={(event) => event.preventDefault()}>
                <DialogTrigger className='flex items-center w-full'>
                    <LockClosedIcon className='mr-3' />
                    {isSuspended ? 'Återaktivera användare' : 'Inaktivera användare'}
                </DialogTrigger>
            </DropdownMenuItem>
            <DialogContent className="flex flex-col gap-6">
                <DialogHeader className='text-left'>
                    <DialogTitle>
                        {isSuspended ? 'Återaktivera' : 'Inaktivera'} {user.first_name} {user.last_name}
                    </DialogTitle>
                    <DialogDescription>
                        Är du helt säker?
                    </DialogDescription>
                </DialogHeader>
                {!isSuspended && (
                    <div className='flex flex-col gap-2'>
                        <Label htmlFor="reason">Anledning till inaktivering:</Label>
                        <Input
                            id='reason'
                            name='reason'
                            required
                            value={reason}
                            onChange={handleReasonChange}
                        />
                    </div>
                )}
                <div className='grid grid-cols-2 gap-3'>
                    <DialogClose asChild>
                        <Button type='button' variant='secondary'>Avbryt</Button>
                    </DialogClose>
                    <Button
                        variant={isSuspended ? 'default' : 'destructive'}
                        onClick={isSuspended ? handleActivate : handleSuspend}
                        disabled={isLoading}>
                        {(isLoading) ? <UpdateIcon className='loader-spinner' /> : <>{isSuspended ? 'Återaktivera användare' : 'Inaktivera användare'}</>}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SuspendMemberDialog;
